// Fuel efficiency calculations for the Utilities → Fuel Efficiency module.
// Entries are expected newest-first (ordered by date desc, created_at desc),
// exactly as they are fetched from the `fuel_entries` table.

export const VEHICLE_TYPES = ['Two Wheeler', 'Four Wheeler', 'Other'];

// Mileage (km/l) for one fill: distance since the previous fill ÷ fuel added now.
// Only meaningful when this fill topped the tank up to full.
export const calculateMileage = (currentEntry, previousEntry) => {
  if (!previousEntry || !currentEntry.is_full_tank) return null;

  const distance = currentEntry.odometer_reading - previousEntry.odometer_reading;
  const mileage = distance / currentEntry.fuel_quantity;

  return mileage > 0 && isFinite(mileage) ? mileage : null;
};

// Summary across all fills of a vehicle. Needs at least two entries.
// - avgMileage: average of full-tank → full-tank fills
// - totalDistance: newest odometer − oldest (baseline) odometer
// - totalFuel: fuel used by the fills that produced a mileage figure
// - totalCost: every fill, including the baseline
// - costPerKm: all fills except the baseline ÷ totalDistance
export const calculateEfficiencyStats = (entries) => {
  if (!entries || entries.length < 2) return null;

  const oldest = entries[entries.length - 1];
  const newest = entries[0];
  const totalDistance = newest.odometer_reading - oldest.odometer_reading;

  const totalCost = entries.reduce((sum, entry) => sum + entry.total_cost, 0);
  // The baseline fill's fuel was burnt before tracking started, so exclude it from cost/km
  const costForDistance = totalCost - oldest.total_cost;

  let mileageSum = 0;
  let mileageCount = 0;
  let totalFuel = 0;
  for (let i = 0; i < entries.length - 1; i++) {
    const current = entries[i];
    const previous = entries[i + 1];
    if (current.is_full_tank && previous.is_full_tank) {
      const mileage = (current.odometer_reading - previous.odometer_reading) / current.fuel_quantity;
      if (mileage > 0 && isFinite(mileage)) {
        mileageSum += mileage;
        mileageCount += 1;
        totalFuel += current.fuel_quantity;
      }
    }
  }

  return {
    avgMileage: mileageCount > 0 ? mileageSum / mileageCount : 0,
    totalDistance,
    totalFuel,
    totalCost,
    costPerKm: totalDistance > 0 ? costForDistance / totalDistance : 0,
    entryCount: entries.length,
  };
};

// Local YYYY-MM-DD (avoids toISOString(), which shifts the date in IST before 05:30)
export const toDateString = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

// Parse a stored YYYY-MM-DD as a local date (new Date('YYYY-MM-DD') would be UTC)
export const fromDateString = (value) => {
  if (!value) return new Date();
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const formatFuelDate = (value) =>
  fromDateString(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export const formatRupees = (amount, digits = 2) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: digits })
    .format(amount || 0);

export const formatNumber = (value, digits = 0) =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value || 0);
